import { useTheme } from "arkh";
import React, { useMemo } from "react";

import { Image, View } from "react-native";

import { useWidgetLayout } from "../WidgetLayoutContext";
import { graphql, useFragment } from "react-relay";

import { WidgetNotePageBackground_widgetNotePage$key } from "./__generated__/WidgetNotePageBackground_widgetNotePage.graphql";

export function WidgetNotePageBackground(props: {
  page: WidgetNotePageBackground_widgetNotePage$key;
}) {
  const { unitSize, height, width } = useWidgetLayout();

  const page = useFragment(
    graphql`
      fragment WidgetNotePageBackground_widgetNotePage on WidgetNotePage {
        backgroundColor
        image {
          id
          largeImage: size(minHeight: 1080, minWidth: 1080) {
            url
            height
            width
          }
        }
        imageScale
        imageTranslateX
        imageTranslateY
        remoteImageURL
        containImage
      }
    `,
    props.page
  );

  const imageDimensions = useMemo(
    () => ({
      width: page.image?.largeImage?.width || 1,
      height: page.image?.largeImage?.height || 1,
    }),
    [page.image?.largeImage]
  );

  const scaleFactor = useMemo(() => {
    if (!page?.image?.largeImage) {
      return 1;
    }
    return width / height > imageDimensions.width / imageDimensions.height
      ? width / imageDimensions.width
      : height / imageDimensions.height;
  }, [
    height,
    imageDimensions.height,
    imageDimensions.width,
    page?.image?.largeImage,
    width,
  ]);

  const theme = useTheme();

  if (!page.image?.largeImage?.url) {
    return (
      <View
        style={{
          height: "100%",
          width: "100%",
          position: "absolute",
          backgroundColor: page.backgroundColor ?? theme.surface,
        }}
      />
    );
  }

  if (page.remoteImageURL) {
    return (
      <Image
        source={{ uri: page.remoteImageURL }}
        progressiveRenderingEnabled
        resizeMode="contain"
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          alignSelf: "center",
        }}
      />
    );
  }
  if (page.containImage) {
    return (
      <Image
        source={{ uri: page.image.largeImage.url }}
        progressiveRenderingEnabled
        resizeMode="contain"
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          alignSelf: "center",
        }}
      />
    );
  }

  return (
    <Image
      source={{ uri: page.image.largeImage.url }}
      progressiveRenderingEnabled
      resizeMode="cover"
      style={{
        position: "absolute",
        width: imageDimensions.width * scaleFactor,
        height: imageDimensions.height * scaleFactor,
        alignSelf: "center",
        transform: [
          {
            translateX: (page.imageTranslateX ?? 0) * unitSize,
          },
          {
            translateY: (page.imageTranslateY ?? 0) * unitSize,
          },
          {
            scale: page.imageScale ?? 1,
          },
        ],
      }}
    />
  );
}
